import { FilterType, RangeFilter } from 'interfaces'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { SortBy } from 'services/content/util'
import { ContentType, FEED_TYPE_KEYWORD, VIDEO_TYPE } from 'shared'
import styles from './ContentGridView.pcss'
import ContentTypeFilter from 'components/ContentTypeFilter'
import StockContentSourceFilter from 'components/StockContentSourceFilter'
import { StockContentProvider } from 'interfaces/Content/StockContentProvider'
import RangeFilterSelect from 'components/RangeFilterSelect'
import SortBySelector from './SortBySelector'
import Tooltip from '@mui/material/Tooltip'
import ClearIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import { DebouncedTextField } from 'components/DebouncedTextField'

interface ContentGridViewFiltersProps {
  filter: FilterType
  range: RangeFilter
  sortBy: SortBy
  contentTypes: ContentType[]
  stockContentSource: StockContentProvider
  query: string
  disableSearch: boolean
  hideRangeFilter?: boolean
  hideSorting?: boolean
  feedType?: string
  className?: string
  onQueryChange: (q: string) => void
  onFilterChange: (f: string) => void
  onStockSourceChange: (s: StockContentProvider) => void
  onRangeChange: (r: RangeFilter) => void
  onSortByChange: (s: SortBy) => void
}

export function ContentGridViewFilters(props: ContentGridViewFiltersProps) {
  const {
    filter, hideSorting, feedType, className, contentTypes, stockContentSource, query, hideRangeFilter,
    range, sortBy, disableSearch, onFilterChange, onStockSourceChange, onRangeChange, onSortByChange, onQueryChange
  } = props
  const intl = useIntl()
  const isStockContentFilter = filter.startsWith('stock')
  const showSortBy = !hideSorting && !isStockContentFilter // no sorting for stock content
  let sortByDisabled = false
  let sortByTooltip = ''

  if (filter === VIDEO_TYPE && feedType === FEED_TYPE_KEYWORD) {
    // no sorting for youtube vids
    sortByDisabled = true
    sortByTooltip = intl.formatMessage({ id: 'content.tooltip-no-sorting-vids' })
  }

  const isGoogleFeed = feedType === FEED_TYPE_KEYWORD
  const sortByOptions = isGoogleFeed ? {
    [SortBy.Engagement]: { label: 'Stars' },
    [SortBy.Date]: { label: 'Most Recent' }
  } : undefined

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQueryChange(e.target.value)
  }

  const clearQuery = () => {
    onQueryChange('')
  }

  return (
    <div className={`${styles.filters} ${className || ''}`} data-testid="content-grid-filters">
      <ContentTypeFilter
        selected={filter}
        options={contentTypes}
        onFilterSelected={onFilterChange}
      />
      {isStockContentFilter && (
        <StockContentSourceFilter
          filterType={filter}
          selectedSource={stockContentSource}
          onSelectedSourceChange={onStockSourceChange}
        />
      )}
      {!isStockContentFilter && !hideRangeFilter && (
        <RangeFilterSelect
          range={range}
          onRangeSelected={onRangeChange}
        />
      )}
      {showSortBy && (
        <Tooltip
          title={sortByTooltip}
          children={(
            <div>
              <SortBySelector
                options={sortByOptions}
                value={sortBy}
                className={styles['sort-select']}
                withSharesOption={!isGoogleFeed}
                disabled={sortByDisabled}
                onChange={onSortByChange}
              />
            </div>
          )}
        />
      )}
      <div className={styles['search-container']}>
        <DebouncedTextField
          placeholder="Search..."
          value={query}
          disabled={disableSearch}
          className={styles.input}
          onTextChange={onQueryChange}
        />
        {Boolean(query) && (
          <IconButton size="small" className={styles['btn-clear-search']} onClick={clearQuery}>
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </div>
    </div>
  )
}
